import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Share2, Bookmark, Home } from 'lucide-react';

// Sample blog articles data with images
const staticBlogArticles = [
   {
     id: 'weak-access-management-risks',
     title: 'The Hidden Risks of Weak Access Management',
     excerpt: 'Weak access management leads to unauthorized users gaining access to confidential data. Learn how IAM strengthens security and streams compliance.',
     content: `
Weak access management leads to unauthorized users gaining access to confidential business data, which increases the risk of breaches and regulatory penalties.

## Why IAM is Essential

Adopting Identity and Access Management (IAM) ensures only the right people can reach sensitive resources, strengthens cybersecurity with automation and multi-factor authentication, and streams compliance and onboarding.

### Key Benefits:
- **Strengthened Security**: Automation and MFA reduce human error.
- **Streamlined Compliance**: Easier to prove who has access to what.
- **Efficient Onboarding**: Get new team members productive faster.

## Take Action Today

Review your organization's current access controls and implement a robust IAM solution. 

For tailored guidance, book an obligation-free consultation today.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQFEm-lQ45GwqQ/feedshare-shrink_2048_1536/B56ZrCr17aHQA0-/0/1764202867780?e=1773878400&v=beta&t=nZjT3zMPcuyqE4VeKIV6unCuxSJ8F-mTSakb_4g4Q9w',
     category: 'Security',
     readTime: '3 min read',
     tags: ['IAM', 'Data Security', 'Compliance', 'Business Efficiency']
   },
   {
     id: 'orphaned-privileged-accounts-risk',
     title: 'Unsure how many orphaned privileged accounts exist in your organization?',
     excerpt: '70% of organizations fail to discover all privileged accounts. Learn how Just-in-Time (JIT) access can reduce your attack surface.',
     content: `
According to a recent survey, 70% of organizations failed to discover all privileged accounts, and about 55% reported they do not consistently revoke privileged access after employees leave.

## The Problem: Persistent Privileges

Persistent privileges and orphaned accounts introduce major security risk. Over time, users accumulate excessive permissions, service or contractor accounts linger, and IT often lacks clear visibility into who has elevated access.

## The Solution: Just-in-Time (JIT) Access

A mature IAM / PAM strategy using Just-in-Time (JIT) access helps. Rather than granting permanent administrator rights, users request temporary elevated access when needed, with automation revoking it once the task completes. 

### Benefits of JIT:
- Reduces standing privileges
- Improves auditability
- Tightens the attack surface for both human and machine identities

## Building a Modern IAM Posture

Leading platforms combine JIT access with automated lifecycle management, ensuring that access grants are time-bound, approved, and regularly reviewed. This helps minimize forgotten service accounts and ghost credentials.

Ready to strengthen your IAM with JIT and reduce orphaned privileges? Let's talk about transforming your identity security.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQFaCNKGIC2JWg/feedshare-shrink_800/B56ZrHRbGpI0Ag-/0/1764279960446?e=1773878400&v=beta&t=akqHVGO88FDbP2c38_ECXg48NSl4UfgQ9BEzXugmEmc',
     category: 'Security',
     readTime: '4 min read',
     tags: ['IAM', 'JustInTime', 'PAM', 'Zero Trust']
   },
   {
     id: 'iam-success-pillars',
     title: 'Do you know what IAM success truly looks like?',
     excerpt: 'IAM success is measurable. Discover the four pillars every organization should focus on to deliver real security and efficiency.',
     content: `
Here are four pillars every organization should focus on with measurable targets:

## 1. Access and Security
- Strong authentication with success rate above 95 percent.
- Full adoption of multi factor authentication reaching 100 percent.
- Rapid onboarding and removal of accounts within 24 hours.
- Zero unused or overprivileged identities after quarterly reviews.

## 2. Governance and Compliance
- Access reviewed regularly with over 95 percent completion rate.
- Continuous regulatory alignment maintaining 100 percent compliance.
- No unapproved overrides keeping authorization failures under 5 percent.

## 3. Operations and Experience
- Sign in is user friendly with lockout rates below 2 percent.
- Centralized reporting on all access events via SIEM integration.
- Incidents detected and addressed with mean time to detect under 24 hours.
- Data drives real improvement through monthly metric reviews.

## 4. Monitoring and Visibility
- Real time threat detection with behavioral analytics active.
- Anomaly detection identifying suspicious patterns within minutes.
- Audit logging capturing 100 percent of access events.
- Security incident response time under industry benchmarks.

With these criteria and metrics, IAM delivers real security, compliance, and efficiency while keeping friction for users low.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQHeXzKP12CbJQ/feedshare-shrink_800/B56ZrcIH4iL0Ag-/0/1764629710320?e=1773878400&v=beta&t=YMD1POzm4SRVZHR5DWjtjILhZgXzpUqYGuwuptQJh9Q',
     category: 'Best Practices',
     readTime: '5 min read',
     tags: ['IAM', 'Security Metrics', 'Compliance', 'Zero Trust']
   },
   {
     id: 'visibility-on-service-accounts',
     title: 'Do you have visibility on the service accounts used by your apps?',
     excerpt: 'Until recently many service accounts were created directly in each application. Learn why centralizing service account management is critical for security.',
     content: `
Until recently many service accounts were created directly in each application and not in the identity platform. This meant there was no central record, no clear owner, and no easy way to remove or update them when projects ended, which increased security risk.

Now more teams create and manage service accounts in the identity platform first and then link them to each application. This gives one place to see every service identity, control who can create them, and apply the same lifecycle rules as human users.

## Key Benefits of Centralized Service Account Management:

- **Clear View**: One place to see all service accounts and where they are used.
- **Easier Audits**: Access is visible in one system, making reviews and compliance much simpler.
- **Better Security**: Centralized credential rotation and fewer "forgotten" accounts that could be exploited.

## Modern IAM for Service Identities

Managing service accounts shouldn't be an afterthought. By applying the same rigor to service identities as you do to human users, you significantly reduce your organization's attack surface.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQEyRm9etj8FAg/feedshare-shrink_800/B56Zr7n3_xGkAs-/0/1765158129327?e=1773878400&v=beta&t=2JAZGXUwXwq-F4pkuaRBgFpi5XqegOutL49iz101v0E',
     category: 'Security',
     readTime: '3 min read',
     tags: ['Service Accounts', 'IAM', 'Cloud Security', 'Identity Management']
   },
   {
     id: 'is-your-organization-growing',
     title: 'Is your organization growing?',
     excerpt: 'For many growing teams, growth leads to more helpdesk tickets, slower onboarding, and unclear access. Learn how to manage access effectively.',
     content: `
For many growing teams, this leads to:
- More helpdesk tickets
- Slower onboarding
- Unclear access when people change roles or leave

Access management starts to matter at this stage. Not only for security, but for daily operations as well.

When access is handled well:
- New hires get productive faster
- Teams face fewer interruptions
- Risk stays visible and manageable

## Free Review of Your Current Setup

If you already have an IAM or SSO system in place, we offer a free review of your current setup.

[Start a conversation with us](https://www.ssolabs.io)

It is a simple way to understand what is working today and where small improvements can make a big difference.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQG_6BLLGJkDZg/feedshare-shrink_800/B56ZuW6.YZIcAg-/0/1767763602297?e=1773878400&v=beta&t=q5-l9Ae7fKvuZ8F5cfV5FSHbjvp64WIzTZT3YZiMuMY',
     category: 'Operations',
     readTime: '3 min read',
     tags: ['Growing Teams', 'Operations', 'Access Management', 'Business Growth']
   },
   {
     id: 'is-your-iam-actually-working',
     title: 'You have IAM. But is it actually working for you?',
     excerpt: 'Most organizations are somewhere on the maturity journey—from managing exceptions manually to having IAM that truly enables the business.',
     content: `
Most organizations are somewhere on the maturity journey—from managing exceptions manually to having IAM that truly enables the business.

Where does your organization stand?

## The IAM Maturity Journey

Managing digital identities and access is not a one-time project, but a continuous journey of improvement. Many businesses start with manual processes, which are prone to error and security gaps. As they mature, they move towards automated, policy-driven, and eventually, business-enabling IAM solutions.

### Stage 1: Manual & Reactive
- Access is granted on request
- No formal review process
- High risk of "privilege creep"

### Stage 2: Standardized
- Basic roles are defined
- Some automated provisioning
- Periodic access reviews

### Stage 3: Optimized & Enabling
- Zero Trust principles applied
- Just-in-time access
- IAM as a business accelerator

## Get a complimentary IAM maturity assessment

Ready to find out where you stand and how to reach the next level?

[Get your assessment here](https://idnty.one/contact)
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQFhI3EGAf1oIg/feedshare-shrink_800/B56Zuak_pRG4Ag-/0/1767824949316?e=1773878400&v=beta&t=O4OjCpR3HoI1B4m8Jt4sTWlcATqXTP_66-Rno7E6xC8',
     category: 'Security',
     readTime: '3 min read',
     tags: ['IAM', 'Maturity Assessment', 'Identity Management', 'Business Security']
   },
   {
     id: 'why-your-business-needs-better-access-control',
     title: 'Why your business needs better access control?',
     excerpt: 'Most businesses operate without realizing they are sitting on a major security risk. Learn why access control is critical for your business.',
     content: `
Most businesses operate without realizing they are sitting on a major security risk.

Here is what typically happens: 
1. An employee leaves, but their login still works. 
2. A contractor finishes a project, yet they still have access to your files. 
3. Your accountant can see customer data they do not need. 
4. Multiple people share one admin password for your cloud software. No one knows who approved access or when it was last reviewed.

This is not just messy. It creates real exposure. A disgruntled former employee could delete files. An unmonitored account could be used for fraud. A data leak could happen simply because too many people had access to sensitive information.

And the worst part? You would not even know until the damage is done.

## What is Identity and Access Management?

IAM is simply about controlling who gets into your systems and what they can do once inside. It ensures the right people have the right access, nothing more. Think of it as digital keys to your business. You would not give every employee a master key to your office. The same logic applies online.

## Three simple steps to start today:
1. Secure critical accounts with two-step login for email, banking, and cloud tools.
2. Review and remove access for former employees, contractors, or anyone no longer working with you.
3. Assign one person to approve and monitor who gets access to sensitive systems and data.

## Why This Matters:

Good access control prevents mistakes, protects your reputation, and gives you full visibility into who holds the keys to your business. You do not need a big IT team to get started.

Small businesses are not too small to be targeted. But they can be too prepared to be victims.

## Ready to secure your business the right way?

We help small and medium businesses set up simple, effective identity and access management without the tech overwhelm. [Contact us](https://lnkd.in/gJaDsbEf) and let us walk you through it.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://media.licdn.com/dms/image/v2/D5622AQEVMsO5jUGn6w/feedshare-shrink_800/B56ZvT48dMHsAg-/0/1768786479270?e=1773878400&v=beta&t=42ErCqig_qtAiNWFjWka5_MtnKbu5t0OLJqpBx0G8w4',
     category: 'Security',
     readTime: '4 min read',
     tags: ['Identity Management', 'Access Control', 'SMB Security', 'Cyber Security']
   },
   {
     id: 'identity-management-access-control-smb',
     title: 'Identity Management & Access Control: A Complete Guide for SMBs',
     excerpt: 'Learn how small and medium businesses can implement robust identity management and access control solutions without breaking the budget.',
     content: `
In today's digital landscape, small and medium businesses (SMBs) face the same cybersecurity threats as large enterprises, but often with limited resources. Identity management and access control have become critical components of any business's security strategy.

## Why Identity Management Matters

Every business today manages digital identities - employees, contractors, partners, and customers. Without proper identity management:

- Unauthorized users can access sensitive data
- Compliance violations can result in hefty fines
- Security breaches can damage reputation and trust

## The Core Components of Identity Management

### 1. Identity Verification
Ensuring that users are who they claim to be through multi-factor authentication (MFA), single sign-on (SSO), and biometric verification.

### 2. Access Control
Defining what resources each identity can access and what actions they can perform. This includes:
- Role-based access control (RBAC)
- Attribute-based access control (ABAC)
- Principle of least privilege

### 3. Lifecycle Management
Managing identities from onboarding through offboarding, including:
- Automated provisioning and deprovisioning
- Periodic access reviews
- Credential management

## Best Practices for SMBs

1. **Start with a Strong Foundation**: Implement MFA for all users, especially for admin accounts.

2. **Automate Where Possible**: Use identity management tools that automate repetitive tasks.

3. **Regular Audits**: Conduct quarterly access reviews to ensure appropriate access levels.

4. **Employee Training**: Educate staff on security best practices and phishing prevention.

5. **Zero Trust Architecture**: Never trust, always verify - assume breach mentality.

## Conclusion

Identity management does not have to be complicated or expensive. With the right tools and approach, SMBs can implement enterprise-grade security that protects their assets while enabling business growth.

Ready to strengthen your identity security? Learn more about IDNTY1's identity management solutions.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-26',
     image: 'https://images.unsplash.com/photo-1555949963-aa784d420c4e?w=400&h=300&fit=crop',
     category: 'Security',
     readTime: '5 min read',
     tags: ['Identity Management', 'Access Control', 'SMB Security', 'Zero Trust']
   },
   {
     id: 'smb-security-best-practices',
     title: 'Essential SMB Security Best Practices for 2026',
     excerpt: 'Discover the top security practices that every small and medium business should implement to protect against modern threats.',
     content: `
As cyber threats continue to evolve, small and medium businesses must stay vigilant. Here are the essential security practices for 2026.

## Top Threats Facing SMBs

- Ransomware attacks
- Phishing and social engineering
- Insider threats
- Supply chain attacks
- Cloud misconfigurations

## Key Security Practices

### 1. Implement Multi-Factor Authentication
MFA should be mandatory for all accounts, especially:
- Email accounts
- Financial systems
- Admin dashboards
- VPN access

### 2. Regular Security Training
Your employees are your first line of defense:
- Conduct monthly security awareness training
- Run simulated phishing exercises
- Keep staff updated on latest threats

### 3. Backup Everything
Follow the 3-2-1 backup rule:
- 3 copies of data
- 2 different storage types
- 1 offsite backup

### 4. Keep Systems Updated
Regularly patch and update:
- Operating systems
- Applications
- Firmware
- Dependencies

### 5. Monitor External Threats
Use external threat monitoring to:
- Detect compromised credentials
- Identify brand impersonation
- Monitor for data leaks

## Conclusion

Security is an ongoing process, not a one-time setup. Stay proactive and protect your business.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-20',
     image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=400&h=300&fit=crop',
     category: 'Best Practices',
     readTime: '4 min read',
     tags: ['Security', 'Best Practices', 'SMB', 'Cyber Defense']
   },
   {
     id: 'zero-trust-security-explained',
     title: 'Zero Trust Security: What Every Business Needs to Know',
     excerpt: 'Zero trust is more than a buzzword - it is a fundamental shift in how we think about security. Learn what it means and how to implement it.',
     content: `
Zero Trust has become one of the most talked-about concepts in cybersecurity. But what does it really mean, and how can SMBs implement it?

## The Zero Trust Philosophy

Traditional security models operate on a "castle and moat" approach - once inside the network, users are trusted. Zero Trust flips this on its head:

**Never trust, always verify.**

### Core Principles

1. **Verify Explicitly**: Always authenticate and authorize based on all available data points.

2. **Use Least Privilege Access**: Limit user access with Just-In-Time and Just-Enough-Access.

3. **Assume Breach**: Minimize blast radius and segment access to prevent lateral movement.

## Implementing Zero Trust

### Start with Identity
- Implement strong authentication
- Use multi-factor authentication
- Deploy single sign-on where appropriate

### Secure Your Endpoints
- Endpoint detection and response (EDR)
- Regular patching
- Device compliance checks

### Monitor Everything
- Continuous validation
- Real-time threat detection
- Behavioral analytics

## Benefits for SMBs

- Better protection against breaches
- Improved compliance posture
- Reduced attack surface
- Enhanced visibility

## Conclusion

Zero Trust is not just for enterprises. SMBs can and should implement zero trust principles to protect their assets.
     `,
     author: 'IDNTY1 Team',
     date: '2026-02-15',
     image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400&h=300&fit=crop',
     category: 'Security',
     readTime: '6 min read',
     tags: ['Zero Trust', 'Security', 'Enterprise', 'Cybersecurity']
   }
];

// Simple markdown-like rendering
const renderContent = (content) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;

  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ul') {
        elements.push(<ul key={elements.length} className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
      } else {
        elements.push(<ol key={elements.length} className="list-decimal pl-6 mb-4 space-y-2">{currentList}</ol>);
      }
      currentList = [];
      listType = null;
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(<h1 key={index} className="text-3xl font-bold mb-4 mt-6">{trimmed.slice(2)}</h1>);
    } else if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={index} className="text-2xl font-bold mb-3 mt-6">{trimmed.slice(3)}</h2>);
    } else if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={index} className="text-xl font-semibold mb-2 mt-4">{trimmed.slice(4)}</h3>);
    } else if (trimmed.startsWith('- ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      currentList.push(<li key={index} className="text-muted-foreground">{trimmed.slice(2)}</li>);
    } else if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      currentList.push(<li key={index} className="text-muted-foreground">{trimmed.replace(/^\d+\.\s/, '')}</li>);
    } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      flushList();
      elements.push(<p key={index} className="font-bold mb-2">{trimmed.replace(/\*\*/g, '')}</p>);
    } else if (trimmed) {
      flushList();
      elements.push(<p key={index} className="text-muted-foreground mb-4 leading-relaxed">{trimmed}</p>);
    }
  });

  flushList();
  return elements;
};

const BlogPost = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '/api/blog-posts';
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          const apiArticles = (data.posts || []).map(post => ({
            id: post.id,
            title: post.title,
            heading: post.heading_override || post.title,
            excerpt: post.excerpt,
            content: post.content,
            author: post.author,
            date: post.published_at,
            image: post.image_url || 'https://www.idnty.one/fNew_logo_2.png',
            category: 'Company Update',
            readTime: '3 min read',
            tags: post.tags || ['linkedin'],
            linkedin_url: post.linkedin_url
          }));
          const combinedArticles = [...staticBlogArticles, ...apiArticles].sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          });
          setAllArticles(combinedArticles);
          const foundArticle = combinedArticles.find(a => a.id === id);
          setArticle(foundArticle || null);
        } else {
          const foundStatic = staticBlogArticles.find(a => a.id === id);
          setAllArticles(staticBlogArticles);
          setArticle(foundStatic || null);
        }
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
        const foundStatic = staticBlogArticles.find(a => a.id === id);
        setAllArticles(staticBlogArticles);
        setArticle(foundStatic || null);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="text-center">Loading article...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you are looking for does not exist.</p>
          <Link to="/blog" className="text-[#2563eb] hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = allArticles.filter(a => a.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#f9fafb]">
       {/* Header with Home Link */}
       <header className="bg-white border-b border-gray-200 py-4">
         <div className="container mx-auto px-4">
           <a 
             href="https://www.idnty.one" 
             className="inline-flex items-center text-sm text-gray-600 hover:text-[#2563eb] transition-colors"
           >
             <Home className="w-4 h-4 mr-2" />
             Back to IDNTY1
           </a>
         </div>
       </header>

      {/* Article Header */}
      <header className="bg-gradient-to-b from-[#eff6ff] to-[#f9fafb] py-12">
        <div className="container mx-auto px-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-gray-600 hover:text-[#2563eb] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

           {/* Article Meta */}
           <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
             <span className="bg-[#eff6ff] text-[#2563eb] px-3 py-1 rounded-full font-medium">
               {article.category}
             </span>
             <span className="flex items-center gap-1">
               <Calendar className="w-4 h-4" />
               {new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
             </span>
             <span className="flex items-center gap-1">
               <Clock className="w-4 h-4" />
               {article.readTime}
             </span>
           </div>

           {/* Article Title */}
           <h1 className="text-3xl font-bold mb-6 text-gray-900">
             {article.heading || article.title}
           </h1>

           {/* Author */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#eff6ff] rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-[#2563eb]" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{article.author}</p>
               <p className="text-sm text-gray-500">IDNTY1 Security Team</p>
            </div>
          </div>
        </div>
      </header>

       {/* Featured Image */}
       <section className="container mx-auto px-4 -mt-4">
         <div className="max-w-4xl mx-auto">
           <div className="rounded-xl overflow-hidden h-64 md:h-96 shadow-lg">
             <img 
               src={article.image || 'https://www.idnty.one/fNew_logo_2.png'} 
               alt={article.title}
               className={`w-full h-full ${article.image ? 'object-cover' : 'object-contain p-8'}`}
             />
           </div>
         </div>
       </section>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
             {/* Share & Save */}
             <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-8 border-b border-gray-200">
               <div className="flex items-center gap-4">
                 <button className="flex items-center gap-2 text-gray-500 hover:text-[#2563eb] transition-colors">
                   <Share2 className="w-4 h-4" />
                   Share
                 </button>
                 <button className="flex items-center gap-2 text-gray-500 hover:text-[#2563eb] transition-colors">
                   <Bookmark className="w-4 h-4" />
                   Save
                 </button>
               </div>
               {article.linkedin_url && (
                 <a
                   href={article.linkedin_url}
                   target="_blank"
                   rel="noopener noreferrer"
                   className="inline-flex items-center px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors w-full sm:w-auto justify-center"
                 >
                   View on LinkedIn
                 </a>
                )}
              </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none text-gray-800">
              {renderContent(article.content)}
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-500">Tags:</span>
                {article.tags?.map((tag) => (
                  <span 
                    key={tag}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

{/* Related Articles */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-gray-900">Related Articles</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {relatedArticles.map((relatedArticle) => (
              <Link 
                key={relatedArticle.id}
                to={`/blog/${relatedArticle.id}`}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="h-40 overflow-hidden">
                  <img 
                    src={relatedArticle.image || 'https://www.idnty.one/fNew_logo_2.png'} 
                    alt={relatedArticle.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <span className="text-xs text-[#2563eb] font-medium mb-2 block">
                    {relatedArticle.category}
                  </span>
                  <h3 className="font-semibold mb-2 line-clamp-2 text-gray-900">
                    {relatedArticle.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 bg-white">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          <p>© 2026 IDNTY1. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
